// ---- Temperature data types ----

/**
 * Indoor-temperature averages from the backend `temperature-averages` endpoint.
 *
 * `last1Hour` is the current value. Any window without readings is `null`.
 */
export type TemperatureAverages = {
  last1Hour: number | null;
  last24Hours: number | null;
  last7Days: number | null;
  last30Days: number | null;
};

export type OverviewTemperatures = {
  targetTemperature: number | null;
  currentAvgTemp: number | null;
  avgTemp7d: number | null;
  avgTemp24h: number | null;
  sparkline7d: number[];
  sparkline24h: number[];
};

/** Raw snake_case response from the backend. */
type RawTemperatureAverages = {
  flexstation_uid?: string;
  substation_uid?: string;
  last_1_hour?: number | null;
  last_24_hours?: number | null;
  last_7_days?: number | null;
  last_30_days?: number | null;
};

const toFiniteNumber = (value: unknown): number | null =>
  typeof value === "number" && !Number.isNaN(value) ? value : null;

/**
 * Converts the backend response into the frontend `TemperatureAverages` shape.
 *
 * Keep field mapping here so backend field changes only need one update.
 */
export function normalizeTemperatureAverages(
  raw: RawTemperatureAverages | null | undefined
): TemperatureAverages {
  return {
    last1Hour: toFiniteNumber(raw?.last_1_hour),
    last24Hours: toFiniteNumber(raw?.last_24_hours),
    last7Days: toFiniteNumber(raw?.last_7_days),
    last30Days: toFiniteNumber(raw?.last_30_days),
  };
}

// ---- Sensor count types ----

export type OverviewSensorCounts = {
  sensorCount: number;
  activeSensorCount: number;
};

// ---- Normalization functions ----

export function normalizeTemperatureSeries(
  dataPoints: Array<{ values?: { living_air_temperature?: number } }> | undefined
): number[] {
  if (!dataPoints) return [];
  return dataPoints.reduce<number[]>((acc, dp) => {
    const value = dp?.values?.living_air_temperature;
    if (value != null && !isNaN(value)) {
      acc.push(value);
    }
    return acc;
  }, []);
}

export function normalizeSensorCounts(
  flexStationsData:
    | Array<{
        sensors?: Array<{
          sensor_type_technical_name?: string;
          last_reading_at?: string | null;
        }>;
      }>
    | undefined
): OverviewSensorCounts {
  if (!flexStationsData || !Array.isArray(flexStationsData)) {
    return { sensorCount: 0, activeSensorCount: 0 };
  }

  const allSensors = flexStationsData.flatMap((fs) => fs.sensors || []);
  const tempSensors = allSensors.filter(
    (sensor) => sensor.sensor_type_technical_name === "living_air_temperature"
  );

  const oneHourAgo = Date.now() - 60 * 60 * 1000;
  const activeSensorCount = tempSensors.filter((sensor) => {
    if (!sensor.last_reading_at) return false;
    return new Date(sensor.last_reading_at).getTime() > oneHourAgo;
  }).length;

  return { sensorCount: tempSensors.length, activeSensorCount };
}
